import React, { useState, useCallback } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { tracker } from 'app-components/utils/core';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/WarningRounded';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { actionHelpers } from 'app-components/flux/core';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors, freeToSpendTypes } from 'app-components/flux/free-to-spend';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import plannedSpendingIcon from 'assets/illustrations/Bill.png';
import RolloverToggle from 'components/Dialogs/EditPlannedSpendingSeries/RolloverToggle';
import StdDialog from 'components/Dialogs/StdDialog';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import CategoryField from 'components/QuickenControls/CategoryField';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { deletePlannedSpendingDialog } from '../DeletePlannedSpending/actions';

const useStyles = makeStyles()((theme) => ({
  iconCat: {
    height: 40,
    width: 40,
    paddingRight: 12,
  },
  textSecondaryColor: {
    color: theme.palette.text.secondary,
  },
  dialog: {
    height: 510,
    width: 850,
    margin: 'auto',
  },
  catAndAmt: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  helperText: {
    maxWidth: '288px',
    width: '288px',
    display: 'flex',
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
  },
  spacer: {
    width: 24,
  },
  paddingLeft: {
    paddingLeft: 8,
  },
  widthOfTextField: {
    width: '288px',
  },
  amountField: {
    textAlign: 'left !important',
  },
  dialogContent: {
    height: 510,
    overflow: 'hidden',
    paddingTop: '16px !important',
  },
  suggested: {
    padding: 12,
    borderRadius: 12,
    width: '65%',
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    marginTop: 8,
  },
  show: {
    opacity: '1 !important',
  },
  delete: {
    color: theme.palette.amount.negative,
  },
  plannedIcon: {
    height: 100,
    width: 100,
  },
  subtitleButton: {
    color: theme.palette.text.secondary,
  },
  warningIcon: {
    alignSelf: 'top',
    marginRight: 5,
    color: theme.palette.warning.main,
    width: 20,
    height: 20,
  },
  warningBox: {
    marginTop: 15,
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 12,
    padding: '8px 16px 8px 16px',
    width: 320,
    backgroundColor: theme.palette.warning.light,
  },
  warningText: {
    color: theme.palette.warning.dark,
  },
  rolloverWrapper: {
    marginRight: theme.spacing(4),
  },
}));

const defaultPlanned = Object.freeze({ name: '', targetAmount: 0, recurring: true });
const pastCutoff = DateTime.local().startOf('month').minus({ months: 6 }).toSeconds();
const currCutoff = DateTime.local().startOf('month').toSeconds();

function EditPlannedSpendingSeries(props) {
  const { freeToSpendId, cardIndex, startDate, filterId, spendingThisMonth, location, editSeries } = props;

  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const futureRollovers = useSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'),
  );
  const plannedItems = useSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const doAllItemsExist = useSelector((state) =>
    freeToSpendSelectors.doPlannedSpendingItemsForDateAllHaveIds(state, startDate),
  );
  const filter = useSelector((state) => filtersSelectors.getFilter(state, filterId));
  const freeToSpend = useSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const leftToSpend = useSelector((state) => freeToSpendSelectors.getLeftToSpend(state, freeToSpend));
  const calculatedPlannedAmount = useSelector((state) =>
    freeToSpendSelectors.getCalculatedPlannedSpending(state, freeToSpendId),
  );
  const parentCoaId = useSelector((state) => categoriesSelectors.getParentFromCoaList(state, filter));

  // Todays date formatted
  const todayDateUnformatted = useSelector(dateSelectors.getCurrentDateString);
  const todayDate = DateTime.fromISO(todayDateUnformatted).startOf('month').toISODate();

  // This contains all the info on the planned item we will update rollovers for. We try to get todays' dates month, but if it doesn't exist, we grab the next one.
  const f2sRolloverUpdateItem = useSelector((state) =>
    freeToSpendSelectors.getEarliestPlannedSpendingItemNotInPastByFilterId(state, filterId),
  );
  const rolloverEditDate = futureRollovers && startDate >= todayDate ? startDate : f2sRolloverUpdateItem?.date;

  const rollover = useSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemRolloverInfo(
      state,
      f2sRolloverUpdateItem?.selectedItem?.id,
      filterId,
      rolloverEditDate,
    ),
  );

  const plannedItem = plannedItems?.get(cardIndex) || defaultPlanned;
  const { name } = plannedItem;
  const [isEditingSeries, setIsEditingSeries] = useState(editSeries);
  const realTargetAmount = !isEditingSeries ? plannedItem.overwrittenTargetAmount : plannedItem.targetAmount;
  const [nameState, setName] = useState(name);
  const [targetAmountState, setTarget] = useState(
    isEditingSeries || plannedItem.overwrittenTargetAmount == null
      ? plannedItem.targetAmount
      : plannedItem.overwrittenTargetAmount,
  );

  const initResetRolloverAmount =
    typeof f2sRolloverUpdateItem?.selectedItem?.resetRolloverAmount === 'number'
      ? f2sRolloverUpdateItem?.selectedItem?.resetRolloverAmount
      : rollover;

  const initIsRolledOver = freeToSpendTypes.validRolloverStates.includes(
    f2sRolloverUpdateItem?.selectedItem?.rolloverType,
  );

  const [resetRolloverAmount, setResetRolloverAmount] = useState(initResetRolloverAmount);
  const [isRolledOver, setIsRolledOver] = useState(initIsRolledOver);

  // eslint-disable-next-line no-unused-vars
  const [_validate, coa, coaSetter, errorText, currCategory, category, _isAlreadyTracked, children] =
    usePlannedCategoryChecker({
      id: parentCoaId,
      startDate,
      isEdit: true,
    });

  // These are to track if the user changed the rollover amount on the updateItem
  const resetDidNotChange = !isRolledOver || initIsRolledOver || resetRolloverAmount === initResetRolloverAmount;
  const rolloversDidNotChange = rolloverEditDate ? isRolledOver === initIsRolledOver : true;

  const isDisabled =
    realTargetAmount === targetAmountState &&
    name === nameState &&
    coa?.id === category?.id &&
    rolloversDidNotChange &&
    resetDidNotChange;

  const isDisabledOrError = isDisabled || !!errorText || targetAmountState <= 0;
  const coaDisplayName = coa?.id === '0' ? 'Uncategorized' : currCategory?.name || 'this category';
  const { averageSpending, numMonths } = useSelector((state) =>
    freeToSpendSelectors.getAverageSpendingForCategory(state, { coaId: coa?.id, pastCutoff, currCutoff }),
  );

  const canUpdateRollover = isEditingSeries && rolloverEditDate;

  // This tracks if the plannedSpending item the user clicked into has rollovers
  const isThisInstanceRolledOver = freeToSpendTypes.validRolloverStates.includes(plannedItem?.rolloverType);

  const { classes, cx } = useStyles();

  const dispatch = useDispatch();

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item) => item !== currCategory?.knownCategoryId));
    }
  };

  const updateF2S = () => {
    const changed = [];
    if ((targetAmountState !== 0 && !targetAmountState) || targetAmountState < 0 || !coa.id) {
      return;
    }
    const item = plannedItems.get(cardIndex);
    const rolloverItem = f2sRolloverUpdateItem?.selectedItem;
    if (!item) {
      return;
    }
    if (nameState !== item.name) {
      changed.push('name');
      item.name = nameState;
      if (rolloverItem) rolloverItem.name = nameState; // We update the rollover item too because the selected item is ALWAYS either on the same date, or in the past.
    }
    if (coa?.id !== category?.id) {
      changed.push('category');
      dispatch(
        filtersActions.updateFilter({
          id: filterId,
          filterItems: [
            {
              type: 'CHARTOFACCOUNT',
              chartOfAccounts: [coa?.toJS?.() || coa, ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? [])],
            },
          ],
        }),
      );
    }

    if (!rolloversDidNotChange) {
      tracker.track(tracker.events.rolloversOptIn, {
        initiator: 'edit',
        value: isRolledOver,
        category: currCategory?.name || 'Uncategorized',
      });

      if (isRolledOver) {
        tracker.track(tracker.events.startingRollover, {
          initiator: 'edit',
          sign: resetRolloverAmount >= 0 ? 'positive' : 'negative',
          amount: resetRolloverAmount,
        });
      }
    }

    // This is for editing the selected item the user clicked into!!
    if (rolloverEditDate === startDate && canUpdateRollover && isEditingSeries) {
      item.rolloverType = isRolledOver
        ? freeToSpendTypes.RolloverTypeEnum.BOTH
        : freeToSpendTypes.RolloverTypeEnum.NONE;

      if (rolloversDidNotChange) {
        // If the user did not change rollover state
        if (isRolledOver && !resetDidNotChange) {
          // And the user also changed the reset amount
          item.resetRolloverAmount = resetRolloverAmount;
        }
      } else if (isRolledOver) {
        item.resetRolloverAmount = resetRolloverAmount;
      }
    }

    if (targetAmountState !== realTargetAmount) {
      changed.push('amount');
      if (isEditingSeries) {
        // reset the overwritten target amount when user edits the series
        if (item.overwrittenTargetAmount || item.overwrittenTargetAmount === 0) {
          item.resetOverwrittenTargetAmount = true;
          if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
        }
        item.targetAmount = targetAmountState;
        if (rolloverItem) rolloverItem.targetAmount = targetAmountState;
      } else {
        item.overwrittenTargetAmount = targetAmountState;
      }
    }
    tracker.track(tracker.events.budgetItemEdit, {
      location,
      fields: changed,
    });
    const spendingItems = plannedItems.splice(cardIndex, 1, item); // TODO: change this to rely on first finding the item

    let total = 0;
    spendingItems?.forEach((plannedValue) => {
      const spentAmount = -1 * plannedValue.calculatedSpentAmount;
      if (
        (plannedValue.overwrittenTargetAmount === 0 || plannedValue.overwrittenTargetAmount) &&
        plannedValue.overwrittenTargetAmount >= spentAmount
      ) {
        total += plannedValue.overwrittenTargetAmount;
      } else if (plannedValue.targetAmount > spentAmount) {
        total += plannedValue.targetAmount;
      } else {
        total += spentAmount;
      }
    });
    const leftToSpendAmount = leftToSpend - calculatedPlannedAmount - total;

    // We update the selected item first!
    actionHelpers
      .bindPromiseAction(
        dispatch,
        freeToSpendActions.updateFreeToSpend,
      )({
        id: freeToSpendId,
        plannedSpendingItems: spendingItems,
        calculatedPlannedSpendingAmount: -1 * total,
        leftToSpendAmount,
      })
      .then((_resp) => {
        const f2sObject = f2sRolloverUpdateItem?.f2sObject;

        // This is for editing the current date item if rollovers require it!!
        if (
          canUpdateRollover &&
          rolloverEditDate !== startDate &&
          isEditingSeries &&
          (!resetDidNotChange || !rolloversDidNotChange)
        ) {
          rolloverItem.rolloverType = isRolledOver
            ? freeToSpendTypes.RolloverTypeEnum.BOTH
            : freeToSpendTypes.RolloverTypeEnum.NONE;

          if (rolloversDidNotChange) {
            // If the user did not change rollover state
            if (isRolledOver && !resetDidNotChange) {
              // And the user also changed the reset amount
              rolloverItem.resetRolloverAmount = resetRolloverAmount;
            }
          } else if (isRolledOver) {
            rolloverItem.resetRolloverAmount = resetRolloverAmount;
          }

          // Go through the items and only edit the one we want to edit in the f2sObject!!
          const newSpendingItems = f2sObject.plannedSpendingItems.map((myItem) => {
            if (myItem.filterId === rolloverItem.filterId) {
              return rolloverItem;
            }
            return myItem;
          });
          dispatch(freeToSpendActions.updateFreeToSpend({ id: f2sObject?.id, plannedSpendingItems: newSpendingItems }));
        }
        removeCategoryFromSuggestedList();
      })
      .catch((error) => assert(false, error));
  };

  const setPlannedName = useCallback((e) => {
    setName(e.target.value);
  }, []);

  const onHandleClose = (event, reason) => {
    if (typeof event?.stopPropagation === 'function') {
      event?.stopPropagation();
    }
    if (typeof event?.preventDefault === 'function') {
      event?.preventDefault();
    }
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      updateF2S();
    }
    dispatch(rootUiActions.removeDialog());

    // editHide();
  };

  const filterFn = (c) =>
    c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
    (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isExpenseCat(null, c.id)) ||
    c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED; // only expense categories or uncat

  const categoryItem = (
    <Box flexDirection={'column'}>
      {!isEditingSeries && (
        <Typography variant='paragraphRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
          Category
        </Typography>
      )}
      <CategoryField
        variant='bodyRegular'
        id='monthly'
        editable={isEditingSeries}
        textFieldVariant='filled'
        filterFn={filterFn}
        value={coa}
        onChange={(c) => coaSetter(c, plannedItem)}
        width='288px'
        margin='normal'
        error={!!errorText}
        helperText={errorText}
        label='Category'
        textFieldProps={{
          label: 'Category',
          variant: 'filled',
          margin: 'normal',
        }}
      />
      {!errorText && isEditingSeries && (
        <Typography variant='paragraphRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
          Spending in this category will be applied to the planned funds.
        </Typography>
      )}
    </Box>
  );

  const topWidth = 288;
  const formView = (
    <Box>
      <Typography variant='bodyRegular' component={'span'} className={classes.subtitleButton}>
        {isEditingSeries ? 'Plan for recurring spending in a specific category every month.' : ''}
      </Typography>
      {!isEditingSeries ? (
        <Box flexDirection='row' display='flex' justifyContent='flex-start' width={topWidth}>
          {nameState ? (
            <div>
              <Typography variant='paragraphRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
                Expense Name
              </Typography>
              <Typography variant='body' className={cx(classes.helperText)}>
                {nameState}
              </Typography>
            </div>
          ) : null}
          {categoryItem}
        </Box>
      ) : null}
      {!isEditingSeries ? (
        <Box width={topWidth} marginTop={1} marginBottom={2}>
          <Typography variant='captionRegular' className={classes.textSecondaryColor}>
            Changes to the expense name and category can be made by editing the expense series.
          </Typography>
        </Box>
      ) : null}
      {isEditingSeries ? (
        <TextField
          margin='normal'
          id='name'
          label={'Expense name (optional)'}
          inputProps={{ maxLength: 30 }}
          variant='filled'
          value={nameState}
          onChange={setPlannedName}
          className={classes.widthOfTextField}
          disabled={!isEditingSeries}
        />
      ) : null}
      <div className={classes.catAndAmt}>
        {isEditingSeries ? categoryItem : null}
        {isEditingSeries ? <div className={classes.spacer} /> : null}
        <Box flexDirection={'column'}>
          <QAmountField
            editable
            id='target-amount'
            variant={'filled'}
            label={'Amount'}
            value={targetAmountState}
            onChange={(e) => setTarget(Number(e.target.value))}
            textFieldVariant={'filled'}
            className={classes.amountField}
            error={targetAmountState < 0}
            expectedSign='positive'
            margin='normal'
          />
          {!isEditingSeries && (
            <div className={classes.info}>
              <InfoOutlinedIcon />
              <Typography variant='paragraphRegular' className={classes.paddingLeft}>
                {`${numberUtils.formatNumber(
                  parseFloat(Math.abs(spendingThisMonth)),
                  'USD',
                  '0,0.00',
                )} spent in ${coaDisplayName} this month.`}
              </Typography>
            </div>
          )}
          {!isEditingSeries && isThisInstanceRolledOver ? (
            <Box className={classes.warningBox}>
              <WarningIcon className={classes.warningIcon} />
              <Typography variant='paragraphRegular' className={classes.warningText}>
                Changing this month’s planned amount will affect the amount rolled over.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </div>
      <Box flexDirection={'column'} display={'flex'} justifyContent={'space-between'} height={120}>
        {isEditingSeries && (
          <div className={cx(classes.suggested, coa.id && Math.abs(averageSpending || 0) > 0 && classes.show)}>
            {currCategory?.logoUrl && <img alt='cat-icon' src={currCategory?.logoUrl} className={classes.iconCat} />}
            <Typography variant='paragraphRegular' className={classes.textColor}>
              {`${numberUtils.formatNumber(
                parseFloat(Math.abs(averageSpending || 0)),
                'USD',
                '0,0.00',
                false,
              )} suggested for ${coaDisplayName} based on spending over the last ${
                numMonths > 1 ? numMonths : ''
              } month${numMonths > 1 ? 's' : ''}.`}
            </Typography>
          </div>
        )}
        {canUpdateRollover ? (
          <div className={classes.rolloverWrapper}>
            <RolloverToggle
              initRolloverSetting={initIsRolledOver}
              isRolledOver={isRolledOver}
              setIsRolledOver={setIsRolledOver}
              startDate={rolloverEditDate}
              resetRolloverAmount={resetRolloverAmount}
              setResetRolloverAmount={setResetRolloverAmount}
            />
          </div>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <StdDialog
      classes={{ root: classes.dialog }}
      onClose={onHandleClose}
      onKeyDown={(e) => {
        if (e.code === 'Enter' && !isDisabledOrError) {
          onHandleClose(e, 'ENTER_KEY_DOWN');
        }
      }}
      open
      showCloseButton
      size='md'
      title={isEditingSeries ? 'Edit expense series' : "Edit this month's expense"}
    >
      <DialogContent className={classes.dialogContent}>
        <Box display={'flex'}>
          <Box height={80} minWidth={80} alignItems={'center'} justifyContent={'center'} display={'flex'}>
            <img src={plannedSpendingIcon} alt={'planned-icon'} className={classes.plannedIcon} />
          </Box>
          <Box padding={5} paddingTop={2}>
            {formView}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display={'flex'} justifyContent={'space-between'} width={'100%'}>
          {!isEditingSeries ? (
            <QButton
              id={'edit-planned-series-button'}
              onClick={() => {
                setIsEditingSeries(true);
                setTarget(plannedItem.targetAmount);
              }}
            >
              Edit series
            </QButton>
          ) : (
            <Box />
          )}
          <Box>
            {isThisInstanceRolledOver && !isEditingSeries ? null : (
              <QButton
                disabled={!isEditingSeries && !doAllItemsExist}
                className={classes.delete}
                type='submit'
                id={'edit-delete-button'}
                onClick={() => {
                  dispatch(rootUiActions.removeDialog());
                  if (isEditingSeries) {
                    dispatch(deletePlannedSpendingDialog(plannedItem.filterId, freeToSpendId, '', true));
                  } else {
                    dispatch(deletePlannedSpendingDialog(plannedItem.filterId, freeToSpendId, '', false));
                  }
                }}
              >
                DELETE
              </QButton>
            )}
            <QButton
              variant='contained'
              type='submit'
              id={'update-button'}
              onClick={(e) => {
                onHandleClose(e, 'SUBMIT_BUTTON');
              }}
              disabled={isDisabledOrError}
              style={{ marginLeft: 8 }}
            >
              UPDATE
            </QButton>
          </Box>
        </Box>
      </DialogActions>
    </StdDialog>
  );
}

EditPlannedSpendingSeries.propTypes = {
  cardIndex: PropTypes.number,
  freeToSpendId: PropTypes.string,
  startDate: PropTypes.string,
  filterId: PropTypes.string,
  spendingThisMonth: PropTypes.number,
  location: PropTypes.string,
  editSeries: PropTypes.bool,
};

export default React.memo(EditPlannedSpendingSeries);
